import React, { useState, useEffect, useRef } from "react";
import { Dropdown, Overlay } from "react-bootstrap";
import { FaPalette, FaCheck, FaChevronLeft } from "react-icons/fa";
import Hint from "../shared/Hint.jsx";
import { updateTheme } from "../../services/apiClient.js";
export { applyTheme } from "../../lib/theme.js";
import {
  applyTheme,
  readTheme,
  themeStorageKey,
  themeGroups,
} from "../../lib/theme.js";
const ThemeSelector = ({ user, setCurrentUser }) => {
  const [currentTheme, setCurrentTheme] = useState(() => readTheme(user));
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const triggers = useRef({});
  const groupMenu = useRef(null);
  const focusOption = useRef(false);
  const closeTimer = useRef(undefined);
  const saveQueue = useRef(Promise.resolve());
  const group = themeGroups.find((item) => item.id === openGroup);
  const cancelClose = () => {
    clearTimeout(closeTimer.current);
  };
  const closeGroup = () => {
    cancelClose();
    focusOption.current = false;
    setOpenGroup(null);
  };
  const leaveGroup = () => {
    cancelClose();
    // Allow the pointer to cross the small gap to the floating submenu.
    closeTimer.current = setTimeout(closeGroup, 200);
  };
  const showGroup = (id, focus = false) => {
    cancelClose();
    focusOption.current = focus;
    setOpenGroup(id);
    if (focus && openGroup === id)
      (
        groupMenu.current?.querySelector('[aria-checked="true"]') ||
        groupMenu.current?.querySelector("button")
      )?.focus();
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    setCurrentTheme(readTheme(user));
  }, [user?.id, user?.theme]);
  const saveTheme = async (theme) => {
    if (!user) return;
    try {
      const updated = await updateTheme(theme);
      // A slow save must not repaint an older choice over a newer one.
      if (document.documentElement.dataset.theme === theme)
        setCurrentUser(updated);
    } catch {
      /* Preserve the per-user local fallback while offline. */
    }
  };
  const changeTheme = (theme) => {
    setCurrentTheme(theme);
    try {
      localStorage.setItem(themeStorageKey(user), theme);
    } catch {
      /* Storage may be disabled. */
    }
    closeGroup();
    setMenuOpen(false);
    // Keep server preference writes in selection order on a slow connection.
    // The visual change above remains immediate.
    saveQueue.current = saveQueue.current.then(() => saveTheme(theme));
  };
  const handleOptionKey = (event) => {
    const items = Array.from(
      groupMenu.current?.querySelectorAll("button") || [],
    );
    const index = items.indexOf(document.activeElement);
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? items.length - 1
            : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) %
              items.length;
      items[next]?.focus();
    } else if (["Escape", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      const trigger = openGroup && triggers.current[openGroup];
      closeGroup();
      trigger?.focus();
    } else if (event.key === "Tab") {
      closeGroup();
      setMenuOpen(false);
      document.getElementById("theme-dropdown")?.focus();
    }
  };
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);
  return (
    <Dropdown
      align="end"
      show={menuOpen}
      onToggle={(show, metadata) => {
        // The submenu is portalled outside the scrollable main menu. Its clicks
        // are still inside this picker, including on touch devices.
        if (
          !show &&
          groupMenu.current?.contains(metadata.originalEvent?.target)
        )
          return;
        setMenuOpen(show);
        if (!show) closeGroup();
      }}
    >
      <Hint label="Change theme" placement="bottom">
        <Dropdown.Toggle
          variant="outline-warning"
          id="theme-dropdown"
          aria-label="Change theme"
          className="btn btn-sm no-caret"
        >
          <FaPalette />
        </Dropdown.Toggle>
      </Hint>
      <Dropdown.Menu className="theme-menu" role="menu" onScroll={closeGroup}>
        {themeGroups.map((item) => (
          <button
            key={item.id}
            ref={(node) => {
              triggers.current[item.id] = node;
            }}
            id={`${item.id}-theme-toggle`}
            type="button"
            role="menuitem"
            className={`dropdown-item theme-family-toggle${item.items.some((option) => option.key === currentTheme) ? " active" : ""}`}
            aria-haspopup="menu"
            aria-expanded={openGroup === item.id}
            aria-controls={
              openGroup === item.id ? "theme-options-menu" : undefined
            }
            onMouseEnter={() => showGroup(item.id)}
            onMouseLeave={leaveGroup}
            onFocus={() => {
              if (openGroup && openGroup !== item.id) closeGroup();
            }}
            onClick={() => showGroup(item.id, true)}
            onKeyDown={(event) => {
              if (
                ["ArrowRight", "ArrowLeft", "Enter", " "].includes(event.key)
              ) {
                event.preventDefault();
                event.stopPropagation();
                showGroup(item.id, true);
              }
            }}
          >
            {item.label}
            <FaChevronLeft aria-hidden="true" />
          </button>
        ))}
      </Dropdown.Menu>
      <Overlay
        key={openGroup}
        show={menuOpen && !!group}
        target={openGroup ? (triggers.current[openGroup] ?? null) : null}
        placement="left-start"
        flip
        transition={false}
        popperConfig={{
          strategy: "fixed",
          modifiers: [
            { name: "offset", options: { offset: [0, 4] } },
            {
              name: "flip",
              options: { fallbackPlacements: ["right-start", "bottom-end"] },
            },
            {
              name: "preventOverflow",
              options: {
                padding: 8,
                altAxis: true,
                tether: false,
                rootBoundary: "viewport",
              },
            },
          ],
        }}
      >
        {({ ref, style, placement, hasDoneInitialMeasure }) => (
          <div
            ref={(node) => {
              ref(node);
              groupMenu.current = node;
              if (node && hasDoneInitialMeasure && focusOption.current) {
                focusOption.current = false;
                requestAnimationFrame(() => {
                  if (node.isConnected)
                    (
                      node.querySelector('[aria-checked="true"]') ||
                      node.querySelector("button")
                    )?.focus();
                });
              }
            }}
            id="theme-options-menu"
            className="dropdown-menu show theme-palette-menu"
            role="menu"
            aria-labelledby={`${openGroup}-theme-toggle`}
            // Keep the measuring menu out of the document's scrollable area.
            // Each group measures afresh, so a taller menu cannot inherit the
            // previous menu's position and briefly introduce a page scrollbar.
            style={
              hasDoneInitialMeasure
                ? style
                : {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    opacity: 0,
                    pointerEvents: "none",
                  }
            }
            data-popper-placement={placement}
            onMouseEnter={cancelClose}
            onMouseLeave={leaveGroup}
            onKeyDown={handleOptionKey}
          >
            {group?.items.map(({ key, label, swatch }) => (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={currentTheme === key}
                className={`dropdown-item theme-palette-item${currentTheme === key ? " active" : ""}`}
                onClick={() => {
                  changeTheme(key);
                  document.getElementById("theme-dropdown")?.focus();
                }}
              >
                <span className="theme-palette-label">
                  {swatch && (
                    <span
                      className="theme-palette-swatch"
                      style={{ backgroundColor: swatch }}
                      aria-hidden="true"
                    />
                  )}
                  {label}
                </span>
                <span className="theme-palette-check">
                  {currentTheme === key && <FaCheck aria-hidden="true" />}
                </span>
              </button>
            ))}
          </div>
        )}
      </Overlay>
    </Dropdown>
  );
};
export default ThemeSelector;
